import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Drives `useResolvedDocxBody`'s `reloadKey` for "Import new version".
 *
 * The editor's body is frozen on mount to protect live edits, so an import must
 * bump `reloadKey` — but only once the refetched record actually carries the
 * imported body, or the editor would re-freeze on the stale one.
 *
 * Waiting for the incoming body to CHANGE is not enough: importing the same
 * file again (or one byte-identical to what the record already holds) leaves
 * the body string unchanged, so a change-only trigger never fires and the
 * editor keeps showing the edits the import was meant to replace.
 * `importPersisted` re-runs the check after every import for that case.
 */
export function useImportReload(incomingBody: string | undefined) {
  const [reloadKey, setReloadKey] = useState(0);
  const [importSeq, setImportSeq] = useState(0);
  const pendingBodyRef = useRef<string | null>(null);

  useEffect(() => {
    if (pendingBodyRef.current !== null && incomingBody === pendingBodyRef.current) {
      pendingBodyRef.current = null;
      setReloadKey((k) => k + 1);
    }
  }, [incomingBody, importSeq]);

  /** Call before persisting the import, with the exact base64 body being saved. */
  const expectImportedBody = useCallback((body: string) => {
    pendingBodyRef.current = body;
  }, []);

  /** Call once the import is saved and the refetch has been requested. */
  const importPersisted = useCallback(() => setImportSeq((s) => s + 1), []);

  return { reloadKey, setReloadKey, expectImportedBody, importPersisted };
}
